import { Alert, flash, Input, Modal, SubmitButton } from "@xola/ui-kit";
import axios, { AxiosRequestConfig } from "axios";
import clsx from "clsx";
import { FormField, InputField } from "common";
import { omit } from "lodash";
import React, { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { DelegateRole, Permission, Seller, User, UserRole } from "types";
import { object, string } from "yup";
import { api } from "../../api/api";
import { useSeller, useUser } from "../../hooks/useAccount";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useVisibility } from "../../hooks/useVisibility";
import { isMultiFactorAuthenticationEnabled } from "../../models/User";
import { NoTranslate } from "../Common/NoTranslate";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { MultiFactorAuthenticationInfoModal } from "../Settings/MyProfile/MultiFactorAuthenticationCheck";
import { MultiFactorAuthenticationModal } from "../Settings/MyProfile/MultiFactorAuthenticationModal";

export enum EmailType {
    PRIMARY = "Primary",
    COMMUNICATION = "Communication",
}

interface ChangeEmailButtonProps {
    emailType: EmailType;
}

interface FormValues {
    email: string;
}

export const ChangeEmailButton = ({ emailType }: ChangeEmailButtonProps) => {
    const invalidate = useInvalidate();
    const queryClient = useQueryClient();
    const seller = useSeller();
    const user = useUser();
    const [isOpen, setIsOpen] = useState(false);

    /**
     * Only Seller can modify the email. Delegates & Impersonated admins cannot modify
     */
    const canModifyEmail = user.id === seller.id || user.roles.includes(DelegateRole.GUIDE);
    const isPendingChangeEmailRequest = isPendingOwnerChangeRequest(user, seller);
    const isCommunicationEmail = emailType === EmailType.COMMUNICATION;
    const shouldShowCancelAndResend = isPendingChangeEmailRequest && !isCommunicationEmail;

    const { mutateAsync: cancelOrResend, isLoading } = useMutation(async (email?: string) => {
        /**
         * removing newEmail since we don't need it to resend confirm/cancel change
         * in these cases BE will skip the MFA check
         */
        const payload = { ...omit(user, "newEmail"), resendEmailNotification: !!email, cancelEmailChange: !email };

        await api.put(`/users/${user.id}/changeEmail`, payload);

        invalidate("users", user.id);
        invalidate("sellers", seller.id);
        queryClient.invalidateQueries("account"); // Because `account` is not a collection or has ID

        if (email) {
            flash.success("Please check your mail inbox for a confirmation email");
        } else {
            flash.success("Email change canceled");
        }
    });

    const handleResendConfirmation = async () => {
        await cancelOrResend(user.newEmail);
    };

    const handleCancelEmailChange = async () => {
        await cancelOrResend(undefined);
    };

    return (
        <div className="mb-5">
            <Alert color="primary">
                <div
                    className={clsx("flex justify-between", canModifyEmail && shouldShowCancelAndResend && "flex-col")}
                >
                    <div className="p-1 leading-5">
                        <div className="text-md font-bold">{emailType} Email</div>
                        <NoTranslate
                            as="div"
                            className="text-base"
                            data-testid={`change-email-button-${emailType}-field`}
                        >
                            {isCommunicationEmail ? (
                                seller.communicationEmail
                            ) : (
                                <PrimaryEmail canModifyEmail={canModifyEmail} user={user} seller={seller} />
                            )}
                        </NoTranslate>
                    </div>

                    {canModifyEmail ? (
                        shouldShowCancelAndResend ? (
                            <div className="mt-4 flex flex-row justify-end space-x-2">
                                <SubmitButton
                                    isLoading={isLoading}
                                    variant="outline"
                                    color="secondary"
                                    onClick={handleCancelEmailChange}
                                >
                                    Cancel Email Change
                                </SubmitButton>
                                <SubmitButton isLoading={isLoading} onClick={handleResendConfirmation}>
                                    Resend Confirmation
                                </SubmitButton>
                            </div>
                        ) : (
                            <div className="p-1">
                                <SubmitButton
                                    type="button"
                                    data-testid={`change-email-button-${emailType}-change-button`}
                                    onClick={() => setIsOpen(true)}
                                >
                                    Change
                                </SubmitButton>
                            </div>
                        )
                    ) : null}
                </div>
            </Alert>

            <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
                <ModalContent emailType={emailType} onClose={() => setIsOpen(false)} />
            </Modal>
        </div>
    );
};

interface ModalContentProps {
    emailType: EmailType;
    onClose: () => void;
}

const ModalContent = ({ emailType, onClose }: ModalContentProps) => {
    const user = useUser();
    const config: AxiosRequestConfig = { ignoreUnauthorizedErrorInterceptor: true };
    const seller = useSeller();
    const invalidate = useInvalidate();
    const queryClient = useQueryClient();

    const isCommunicationEmail = emailType === EmailType.COMMUNICATION;
    const { isGranted } = useAuthorization();
    const hasMFAAccess = isGranted(Permission.ADD_MULTI_FACTOR_AUTHENTICATION_VIEW);
    const isMFAEnabled = isMultiFactorAuthenticationEnabled(user);
    const [changeEmailPayload, setChangeEmailPayload] = useState<FormValues>();
    const [showMFAInfo, { toggle: toggleShowMFAInfo }] = useVisibility(false);
    const [showMFAModal, { toggle: toggleShowMFAModal }] = useVisibility(false);

    const isSameEmail = (toUpdateEmail: string) => {
        const currentEmail = isCommunicationEmail ? seller.communicationEmail : seller.email;
        return currentEmail.toLowerCase() === toUpdateEmail.toLowerCase();
    };

    const { mutateAsync: changeEmail, isLoading } = useMutation(async ({ email }: FormValues) => {
        if (isSameEmail(email)) {
            return flash.show({
                text: `Please try again with a different email other than ${email}`,
                color: "danger",
            });
        }

        await (isCommunicationEmail
            ? api.patch(`/sellers/${seller.id}?seller=${seller.id}`, { id: user.id, communicationEmail: email }, config)
            : api.put(`/users/${user.id}/changeEmail?seller=${seller.id}`, { ...user, newEmail: email }, config));

        invalidate("users", user.id);
        invalidate("sellers", seller.id);
        queryClient.invalidateQueries("account"); // Because `account` is not a collection or has ID
        flash.success("Please check your mail inbox for a confirmation email");
    });

    const handleSubmit = async (values: FormValues) => {
        if (hasMFAAccess && !isMFAEnabled) {
            toggleShowMFAInfo();
            return;
        }

        setChangeEmailPayload(values);
        try {
            await changeEmail(values);
        } catch (error: any) {
            if (axios.isAxiosError(error) && error.response?.status === 450) {
                toggleShowMFAModal();
                return;
            }

            throw new Error(error);
        }
    };

    const handleMultiFactorAuthenticationCodeSubmit = async (code: string) => {
        if (code) {
            config.headers = { ...config.headers, "X-AUTH-CODE": code };
            if (changeEmailPayload) {
                await changeEmail(changeEmailPayload);
            }

            onClose();
        }
    };

    // show modal if user has mfa access and enabled and payload contains email
    const shouldShowMFAModel = hasMFAAccess && isMFAEnabled && !!changeEmailPayload && showMFAModal;

    return (
        <XolaFormik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {({ handleSubmit, setFieldValue }) => (
                <>
                    <Modal.Header>Change {emailType} Email</Modal.Header>

                    <Modal.Body>
                        <FormField name="currentEmail" label="Current Email">
                            <Input
                                disabled
                                value={isCommunicationEmail ? seller.communicationEmail : seller.email}
                                data-testid="change-email-button-current-email"
                            />
                        </FormField>

                        <FormField name="email" label="New Email">
                            <InputField
                                placeholder="Email"
                                data-testid="change-email-button-new-email"
                                onChange={(e: any) => setFieldValue("email", e.target.value)}
                            />
                        </FormField>
                    </Modal.Body>

                    <Modal.Footer>
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                        <SubmitButton isLoading={isLoading} onClick={handleSubmit}>
                            Save
                        </SubmitButton>
                    </Modal.Footer>
                    <MultiFactorAuthenticationModal
                        isOpen={shouldShowMFAModel}
                        onClose={() => {
                            setChangeEmailPayload(undefined);
                            toggleShowMFAModal();
                        }}
                        onSuccess={handleMultiFactorAuthenticationCodeSubmit}
                    />

                    <MultiFactorAuthenticationInfoModal
                        isOpen={showMFAInfo}
                        onClose={toggleShowMFAInfo}
                        onConfigure={toggleShowMFAInfo}
                    />
                </>
            )}
        </XolaFormik>
    );
};

const initialValues: FormValues = {
    email: "",
};

const validationSchema = object().shape({
    email: string().trim().email("is not a valid email address").required(),
});

const PrimaryEmail = ({ user, seller, canModifyEmail }: { user: User; seller: Seller; canModifyEmail: boolean }) => {
    return user.newEmail && canModifyEmail ? (
        <span className="mt-3 flex flex-col space-y-2">
            {user.roles.includes(UserRole.SELLER) ? <span>{seller.email}</span> : null}
            <span>
                A confirmation email has been sent to <strong>{user.newEmail}</strong>. Please follow the instructions
                in that email to complete the email change.
            </span>
        </span>
    ) : (
        <span>{seller.email}</span>
    );
};

export const isPendingOwnerChangeRequest = (user: User, seller: Seller): boolean => {
    return user.newEmail ? user.newEmail !== seller.email : false;
};
